import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { insertTeamMemberSchema } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Building2 } from "lucide-react";

export default function TeamMembersPage() {
  const params = useParams();
  const teamId = parseInt(params.id);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const { toast } = useToast();

  // Fetch team details
  const { data: team, isLoading: teamLoading } = useQuery({
    queryKey: [`/api/teams/${teamId}`],
    queryFn: () => apiRequest("GET", `/api/teams/${teamId}`).then(res => res.json()),
    enabled: !isNaN(teamId),
  });

  // Fetch team members
  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: [`/api/teams/${teamId}/members`],
    queryFn: () => apiRequest("GET", `/api/teams/${teamId}/members`).then(res => res.json()),
    enabled: !isNaN(teamId),
  });

  // Fetch all employees for the add dialog
  const { data: allEmployees = [], isLoading: employeesLoading } = useQuery({
    queryKey: ["/api/employees"],
    queryFn: () => apiRequest("GET", "/api/employees").then(res => res.json()),
  });

  // Add a member to the team
  const addMemberMutation = useMutation({
    mutationFn: (data: any) => apiRequest("POST", "/api/team-members", data).then(res => res.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/teams/${teamId}/members`] });
      setIsAddDialogOpen(false);
      toast({
        title: "Success",
        description: "Team member added successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to add team member: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Remove a member from the team
  const removeMemberMutation = useMutation({
    mutationFn: ({ teamId, employeeId }: { teamId: number; employeeId: number }) => 
      apiRequest("DELETE", `/api/team-members/${teamId}/${employeeId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/teams/${teamId}/members`] });
      toast({
        title: "Success",
        description: "Team member removed successfully",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: `Failed to remove team member: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  // Filter out employees that are already members of the team
  const availableEmployees = allEmployees.filter((employee: any) => 
    !members.some((member: any) => member.id === employee.id)
  );

  if (isNaN(teamId)) {
    return (
      <div className="container mx-auto py-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Invalid Team ID</h1>
          <Link href="/teams">
            <Button>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Teams
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <Link href="/teams" className="text-sm text-muted-foreground hover:text-primary flex items-center mb-4">
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Teams
        </Link>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            {teamLoading ? (
              <>
                <Skeleton className="h-10 w-10 rounded" />
                <div>
                  <Skeleton className="h-8 w-40" />
                  <Skeleton className="h-4 w-24 mt-1" />
                </div>
              </>
            ) : (
              <>
                <div className="bg-slate-200 rounded h-12 w-12 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-slate-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">{team?.name}</h1>
                  <p className="text-muted-foreground">
                    {team?.department && `${team.department} · `}
                    <Badge variant={team?.status === "active" ? "default" : "secondary"}>
                      {team?.status === "active" ? "Active" : "Inactive"}
                    </Badge>
                  </p>
                </div>
              </>
            )}
          </div>
          <Button onClick={() => setIsAddDialogOpen(true)} disabled={employeesLoading || availableEmployees.length === 0}>
            <i className="ri-user-add-line mr-2" />
            Add Team Member
          </Button>
        </div>
        
        {!teamLoading && team?.description && (
          <div className="mt-4 text-sm text-muted-foreground">
            {team.description}
          </div>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-medium">Team Members</CardTitle>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">This team doesn't have any members yet.</p>
              <Button onClick={() => setIsAddDialogOpen(true)} disabled={employeesLoading || availableEmployees.length === 0}>
                <i className="ri-user-add-line mr-2" />
                Add Team Member
              </Button>
            </div>
          ) : (
            <div className="border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Job Title</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member: any) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <div className="bg-slate-200 rounded-full h-8 w-8 flex items-center justify-center">
                            {member.fullName.charAt(0).toUpperCase()}
                          </div>
                          <span className="font-medium">{member.fullName}</span>
                        </div>
                      </TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{member.jobTitle || "-"}</TableCell>
                      <TableCell>{member.department || "-"}</TableCell>
                      <TableCell>
                        <Badge
                          variant={member.status === "active" ? "default" : "secondary"}
                        >
                          {member.status === "active" ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (window.confirm(`Are you sure you want to remove ${member.fullName} from this team?`)) {
                              removeMemberMutation.mutate({ teamId, employeeId: member.id });
                            }
                          }}
                        >
                          <i className="ri-delete-bin-line mr-2" />
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Team Member Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Team Member</DialogTitle>
            <DialogDescription>
              Add an employee to {team?.name}.
            </DialogDescription>
          </DialogHeader>
          <AddTeamMemberForm
            teamId={teamId}
            employees={availableEmployees}
            isLoading={employeesLoading}
            onSubmit={(data) => addMemberMutation.mutate(data)}
            isSubmitting={addMemberMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Team member form component
function AddTeamMemberForm({ 
  teamId,
  employees = [],
  isLoading,
  onSubmit,
  isSubmitting,
}: { 
  teamId: number;
  employees: any[];
  isLoading: boolean;
  onSubmit: (data: any) => void; 
  isSubmitting: boolean;
}) {
  // Define form schema
  const formSchema = z.object({
    teamId: z.number(),
    employeeId: z.number({
      required_error: "Please select an employee",
    }),
  });

  // Initialize the form
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      teamId,
      employeeId: undefined as unknown as number,
    },
  });

  // Handle form submission
  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField
          control={form.control}
          name="employeeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Employee</FormLabel>
              <FormControl>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isLoading}
                  {...field}
                  value={field.value || ""}
                  onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                >
                  <option value="">Select an employee</option>
                  {employees.map((employee) => (
                    <option key={employee.id} value={employee.id}>
                      {employee.fullName} - {employee.email}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
              {employees.length === 0 && !isLoading && (
                <p className="text-sm text-amber-600">
                  All available employees are already members of this team.
                </p>
              )}
            </FormItem>
          )}
        />

        <DialogFooter>
          <Button 
            type="submit" 
            disabled={isSubmitting || isLoading || employees.length === 0}
          >
            {isSubmitting ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-2" />
                Adding...
              </>
            ) : (
              "Add to Team"
            )}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}